import {
  Button, CircularProgress, FormControl, FormHelperText, IconButton, InputLabel, MenuItem, Select, Typography, Grid
} from '@material-ui/core';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import ClearIcon from '@material-ui/icons/Clear';
import TableChartOutlinedIcon from '@material-ui/icons/TableChartOutlined';
import _ from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import FiltersContext from '../../contexts/FiltersContext';
import StoryContext from '../../contexts/StoryContext';
import { rdsTabulate } from '../../modules/rds';
import states, { stateFIPS } from '../../modules/states';
import { toFormattedNumber, toPercent } from '../../modules/text';

function CountyComparisonTableNSH() {
  const { stateCode } = useParams();
  const [ready, setReady] = useState(false);
  const { settingType } = useContext(FiltersContext);
  const { primaryColor } = useContext(StoryContext);
  const [selectedCounty, setSelectedCounty] = useState('');
  const [selectedCounties, setSelectedCounties] = useState([]);
  const [stateStats, setStateStats] = useState({});
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);

  useEffect(() => {
    function getProvidersSubsidized() {
      let whereClause = `state_fips=${stateFIPS[stateCode.toUpperCase()]} AND license_type=LIC AND operating_during_nsh=1`;

      if (settingType === 'All') {
        whereClause += ' AND (setting_type=HOM OR setting_type=CEN)';
      } else if (settingType === 'Center-based child care') {
        whereClause += ' AND setting_type=CEN';
      } else {
        whereClause += ' AND setting_type=HOM';
      }

      return rdsTabulate('ccdc', 'provider', {
        dims: 'county_fips',
        measure: 'capacity:COUNT(provider_id)',
        where: whereClause,
        format: 'amcharts',
        inject: true,
      }, true);
    }

    function getProviders() {
      let whereClause = `state_fips=${stateFIPS[stateCode.toUpperCase()]} AND license_type=LIC`;

      if (settingType === 'All') {
        whereClause += ' AND (setting_type=HOM OR setting_type=CEN)';
      } else if (settingType === 'Center-based child care') {
        whereClause += ' AND setting_type=CEN';
      } else {
        whereClause += ' AND setting_type=HOM';
      }

      return rdsTabulate('ccdc', 'provider', {
        dims: 'county_fips',
        measure: 'capacity:COUNT(provider_id)',
        where: whereClause,
        format: 'amcharts',
        inject: true,
      }, true);
    }

    Promise.all([getProviders(), getProvidersSubsidized()])
      .then((results) => {
        const providers = _.orderBy(results[0].parsedBody.dataProvider, ['county_fips'], ['asc']);
        const providersSubsidized = _.orderBy(results[1].parsedBody.dataProvider, ['county_fips'], ['asc']);
        const providersCount = providers.reduce((a, v) => (a + Number.parseInt(v.capacity, 10)), 0);
        const providersSubsidizedCount = providersSubsidized.reduce((a, v) => (a + Number.parseInt(v.capacity, 10)), 0);

        setStateStats({ providersCount, providersSubsidizedCount });

        const payload = providersSubsidized.map((v) => {
          const totals = _.find(providers, { county_fips: v.county_fips }); // find subsidized numbers for the county
          // Object.assign(v, { value: ((v.capacity / providersCount)), providersCount });
          const result = { ...v };
          result.total = totals.capacity;
          result.value = result.capacity / result.total;
          return result;
        });
        // realign data. Update selected counties with fresh data
        const fips = selectedCounties.map((c) => (c.county_fips));
        if (fips.length > 0) {
          setSelectedCounties(_.filter(payload, (o) => fips.includes(o.county_fips)));
        }
        // set data
        setData(payload);
      })
      .catch((rdsError) => {
        setError(rdsError);
      })
      .finally(() => {
        setReady(true);
      });
  }, [stateCode, settingType]);

  const handleSelectionChange = (event) => {
    setSelectedCounty(event.target.value);
  };

  const handleAddCounty = () => {
    if (!_.includes(selectedCounties, selectedCounty)) {
      setSelectedCounties(selectedCounties.concat([selectedCounty]));
    }
    setSelectedCounty('');
  };

  const removeCounty = (c) => () => {
    if (!c) return;
    setSelectedCounties(selectedCounties.filter((f) => (f.county_fips !== c.county_fips)));
  };

  if (!ready) {
    return <CircularProgress />;
  }

  return (
    <>
      {error && <Typography variant="body1" color="error">{error.message}</Typography>}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        color: primaryColor,
      }}
      >
        <TableChartOutlinedIcon />&nbsp;<Typography variant="h5">County Comparison Table</Typography>
      </div>
      <Grid container>
        <Grid item xs={8} sm={6} lg={3}>
          <FormControl>
            <InputLabel id="countySelect">County</InputLabel>
            <Select
              labelId="countySelect"
              id="countySelectHelper"
              value={selectedCounty}
              onChange={handleSelectionChange}
            >
              {data.map((d) => (<MenuItem value={d} key={d.county_fips}>{d.county_fips}</MenuItem>))}

            </Select>
            <FormHelperText>Select a county from the menu to add to the comparison table</FormHelperText>
          </FormControl>
        </Grid>
        <Grid item xs={4} sm={6} lg={9}>
          <Button
            disabled={!selectedCounty}
            variant="contained"
            onClick={handleAddCounty}
            style={{
              marginLeft: '20px', marginBottom: '-70px', backgroundColor: primaryColor, color: '#fff',
            }}
            size="small"
          >Add to table
          </Button>
        </Grid>
      </Grid>
      <Table size="small" aria-label="County comparison table" style={{ overflowX: 'auto', display: 'block' }}>
        <TableHead>
          <TableRow>
            <TableCell>County</TableCell>
            <TableCell align="right">Number of child care providers</TableCell>
            <TableCell align="right">Percent of providers who offer NSH care</TableCell>

            <TableCell>&nbsp;</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow selected>
            <TableCell component="th" scope="row">
              {states[stateCode.toUpperCase()]} (State)
            </TableCell>
            <TableCell align="right">{toFormattedNumber(stateStats.providersCount)}</TableCell>
            <TableCell align="right">{toPercent(stateStats.providersSubsidizedCount / stateStats.providersCount)}</TableCell>
            <TableCell>&nbsp;</TableCell>
          </TableRow>
          {selectedCounties.map((row) => (
            <TableRow key={row.county_fips}>
              <TableCell component="th" scope="row">
                {row.county_fips}
              </TableCell>
              <TableCell align="right">{toFormattedNumber(row.total)}</TableCell>
              <TableCell align="right">{toPercent(row.value)}</TableCell>
              <TableCell><IconButton size="small" onClick={removeCounty(row)} aria-label={`Remove ${row.county}`}><ClearIcon /></IconButton></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  );
}

export default CountyComparisonTableNSH;
